import React, { useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";

const MOTIVOS = [
  { k: "atrasado", label: "Atrasados (no incluidos)", color: "text-orange-300" },
  { k: "sin_fecha", label: "Sin fecha (no incluidos)", color: "text-zinc-300" },
  { k: "fecha_futura", label: "Fuera del periodo / día", color: "text-amber-300" },
  { k: "ya_montada", label: "Ya montados", color: "text-emerald-300" },
  { k: "en_ruta", label: "Ya en ruta", color: "text-cyan-300" },
  { k: "estado_no_elegible", label: "Estado no elegible", color: "text-zinc-400" },
  { k: "material_no_apto", label: "Material no apto", color: "text-rose-300" },
  { k: "sin_geo", label: "Sin geocodificar", color: "text-red-300" },
];

function Kpi({ v, label, cls = "text-zinc-200", testId, hint }) {
  return (
    <div className="text-center" data-testid={testId} title={hint}>
      <div className={`text-2xl font-bold ${cls}`}>{v}</div>
      <div className="text-[10px] uppercase text-zinc-500 leading-tight">{label}</div>
    </div>
  );
}

/** Resumen del análisis previo: candidatos, excluidos por motivo (desplegables) y capacidad. */
export default function ResumenAnalisis({ resumen, excluidos = {}, generado }) {
  const [open, setOpen] = useState(null);
  if (!resumen) return null;
  const ex = resumen.excluidos || {};
  const per = resumen.periodo;
  const otros = (ex.estado_no_elegible || 0) + (ex.material_no_apto || 0) + (ex.sin_geo || 0);
  return (
    <div className="space-y-3" data-testid="resumen-analisis">
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3">
          <div className="text-lg font-bold text-zinc-100" data-testid="kpi-trabajos-destinos">
            {resumen.candidatos} trabajos · {resumen.n_destinos ?? "—"} destinos
          </div>
          {per ? (
            <span className="text-[11px] text-zinc-500">📅 Periodo <b className="text-zinc-300">{per.inicio}</b> → <b className="text-zinc-300">{per.fin}</b>{generado && <> · ruta del <b className="text-amber-300">{per.fecha_ruta}</b>{per.alcance === "dia" ? " (sólo ese día)" : ""}</>}</span>
          ) : (
            <span className="text-[11px] text-zinc-500">📅 Plan: <b className="text-zinc-300">{resumen.fecha_plan}</b> · modo <b className="text-zinc-300">{resumen.modo_fecha}</b></span>
          )}
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-3">
          <Kpi v={resumen.planificables ?? resumen.candidatos} label="En el periodo" cls="text-emerald-300" testId="kpi-planificables" hint="Fecha de montaje dentro de Desde–Hasta" />
          <Kpi v={per ? (resumen.atrasados || 0) : (ex.atrasado || 0)} label={per && resumen.atrasados ? "Atrasados incl." : "Atrasados"} cls="text-orange-300" testId="kpi-atrasados" hint={per ? `${resumen.atrasados || 0} incluidos · ${ex.atrasado || 0} no incluidos` : undefined} />
          <Kpi v={per ? (resumen.sin_fecha || 0) : (ex.sin_fecha || 0)} label={per && resumen.sin_fecha ? "Sin fecha incl." : "Sin fecha"} testId="kpi-sin-fecha" hint={per ? `${resumen.sin_fecha || 0} incluidos · ${ex.sin_fecha || 0} no incluidos` : undefined} />
          <Kpi v={resumen.fuera_ambito || 0} label="Fuera de ámbito" cls="text-zinc-400" testId="kpi-fuera-ambito" />
          <Kpi v={ex.ya_montada || 0} label="Ya montados" testId="kpi-ya-montada" />
          <Kpi v={ex.en_ruta || 0} label="Ya en ruta" cls="text-cyan-300" testId="kpi-en-ruta" />
          <Kpi v={otros + (ex.fecha_futura || 0) + (per ? (ex.atrasado || 0) + (ex.sin_fecha || 0) : 0)} label="Otros excluidos" cls="text-rose-300" testId="kpi-otros" hint="Fuera de periodo, atrasados/sin fecha no incluidos, no elegibles, material no apto, sin GPS" />
          <Kpi v={resumen.k_min} label={`Rutas mín. (máx ${resumen.max_paradas ?? "∞"})`} cls="text-violet-300" testId="kpi-k-min" />
          <Kpi v={resumen.k_generar} label={generado ? "Rutas generadas" : "Rutas a generar"} cls="text-amber-300" testId="kpi-k-generar" />
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px] text-zinc-500">
          {resumen.fuera_campana > 0 && <span>{resumen.fuera_campana} de otras campañas</span>}
          {resumen.ciudades?.length > 0 && <span>Ciudades: {resumen.ciudades.slice(0, 6).map(([c, n]) => `${c} (${n})`).join(", ")}</span>}
        </div>
      </div>

      {resumen.aviso_capacidad && (
        <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-3 text-xs text-amber-200 flex items-start gap-2" data-testid="aviso-capacidad">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {resumen.aviso_capacidad}
        </div>
      )}

      {MOTIVOS.some((m) => (ex[m.k] || 0) > 0) && (
        <div className="bg-zinc-900/30 border border-zinc-800 rounded-xl divide-y divide-zinc-800/70" data-testid="excluidos-lista">
          {MOTIVOS.filter((m) => (ex[m.k] || 0) > 0).map((m) => {
            const items = excluidos[m.k] || [];
            const isOpen = open === m.k;
            return (
              <div key={m.k}>
                <button type="button" onClick={() => setOpen(isOpen ? null : m.k)} data-testid={`excluidos-toggle-${m.k}`}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-zinc-900/60">
                  {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />}
                  <span className={`font-semibold ${m.color}`}>{ex[m.k]}</span>
                  <span className="text-zinc-300">{m.label}</span>
                  <span className="ml-auto text-[10px] text-zinc-600">{isOpen ? "ocultar" : "ver motivo"}</span>
                </button>
                {isOpen && (
                  <ul className="px-3 pb-2 space-y-1 max-h-64 overflow-y-auto" data-testid={`excluidos-detalle-${m.k}`}>
                    {items.length === 0 && <li className="text-[11px] text-zinc-500 italic">Detalle no disponible</li>}
                    {items.map((p) => (
                      <li key={p.linea_id} className="text-[11px] bg-zinc-950/50 rounded px-2 py-1 flex flex-wrap gap-x-2">
                        <span className="font-semibold text-zinc-200">{p.espacio_nombre || "—"}</span>
                        <span className="text-zinc-500">{p.ciudad}</span>
                        <span className="font-mono text-amber-300/80">{p.peticion_codigo}</span>
                        <span className="text-zinc-400 ml-auto">{p.detalle}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
